import { t } from "@/languages/i18n"
import { DeleteOutlined, DownOutlined, HolderOutlined, UpOutlined } from "@ant-design/icons"
import type { TreeDataNode, TreeProps } from "antd"
import { Button, Empty, Tooltip, Tree } from "antd"
import React, { useCallback, useMemo } from "react"
import { MAX_MENU_DEPTH, type MenuNode } from "../types/menu"
import { findNode, maxDepthOf } from "../utils/menu-tree"

interface MenuStructureTreeProps {
  nodes: MenuNode[]
  selectedId: number | null
  disabled?: boolean
  onSelect: (id: number | null) => void
  onDrop: (dragId: number, dropId: number, mode: "before" | "after" | "inside") => void
  onIndent: (id: number) => void
  onOutdent: (id: number) => void
  onMove: (id: number, direction: "up" | "down") => void
  onRemove: (id: number) => void
}

const MenuStructureTree: React.FC<MenuStructureTreeProps> = ({
  nodes,
  selectedId,
  disabled,
  onSelect,
  onDrop,
  onIndent,
  onOutdent,
  onMove,
  onRemove,
}) => {
  /** Cấp của từng node, dùng cho allowDrop để không vượt quá 3 cấp public hỗ trợ. */
  const depthById = useMemo(() => {
    const map = new Map<number, number>()
    const walk = (list: MenuNode[], depth: number) =>
      list.forEach(node => {
        map.set(node.id, depth)
        walk(node.children, depth + 1)
      })
    walk(nodes, 0)
    return map
  }, [nodes])

  const treeData = useMemo<TreeDataNode[]>(() => {
    const toTreeNode = (node: MenuNode): TreeDataNode => ({
      key: node.id,
      title: (
        <div className="flex items-center gap-2 w-full group py-[2px]">
          <span className="truncate font-medium">{node.name || t("Chưa đặt tên")}</span>
          <span className="truncate text-xs text-gray-400 font-mono">{node.url || node.slug}</span>
          <span className="flex-1" />
          <span className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Tooltip title={t("Lên")}>
              <Button
                size="small"
                type="text"
                icon={<UpOutlined />}
                disabled={disabled}
                onClick={event => {
                  event.stopPropagation()
                  onMove(node.id, "up")
                }}
              />
            </Tooltip>
            <Tooltip title={t("Xuống")}>
              <Button
                size="small"
                type="text"
                icon={<DownOutlined />}
                disabled={disabled}
                onClick={event => {
                  event.stopPropagation()
                  onMove(node.id, "down")
                }}
              />
            </Tooltip>
            <Tooltip title={t("Giảm cấp (Shift+Tab)")}>
              <Button
                size="small"
                type="text"
                disabled={disabled}
                onClick={event => {
                  event.stopPropagation()
                  onOutdent(node.id)
                }}
              >
                ⇤
              </Button>
            </Tooltip>
            <Tooltip title={t("Tăng cấp (Tab)")}>
              <Button
                size="small"
                type="text"
                disabled={disabled}
                onClick={event => {
                  event.stopPropagation()
                  onIndent(node.id)
                }}
              >
                ⇥
              </Button>
            </Tooltip>
            <Tooltip title={t("Xóa khỏi menu")}>
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={disabled}
                onClick={event => {
                  event.stopPropagation()
                  onRemove(node.id)
                }}
              />
            </Tooltip>
          </span>
        </div>
      ),
      children: node.children.map(toTreeNode),
    })

    return nodes.map(toTreeNode)
  }, [disabled, nodes, onIndent, onMove, onOutdent, onRemove])

  const handleDrop: TreeProps["onDrop"] = info => {
    const dragId = Number(info.dragNode.key)
    const dropId = Number(info.node.key)
    const positions = info.node.pos.split("-")
    const relative = info.dropPosition - Number(positions[positions.length - 1])

    if (!info.dropToGap) {
      onDrop(dragId, dropId, "inside")
      return
    }

    onDrop(dragId, dropId, relative === -1 ? "before" : "after")
  }

  /** Chặn trước khi thả: nhánh đang kéo cộng vào cấp đích không được quá 3 cấp. */
  const allowDrop: TreeProps["allowDrop"] = ({ dragNode, dropNode, dropPosition }) => {
    const targetDepth = depthById.get(Number(dropNode.key)) ?? 0
    const dragBranchDepth = maxDepthOf(findNode(nodes, Number(dragNode.key)))
    const resultDepth = dropPosition === 0 ? targetDepth + 1 : targetDepth
    return resultDepth + dragBranchDepth <= MAX_MENU_DEPTH - 1
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled || selectedId === null) return

      if (event.key === "Tab") {
        event.preventDefault()
        if (event.shiftKey) onOutdent(selectedId)
        else onIndent(selectedId)
        return
      }

      if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
        event.preventDefault()
        onMove(selectedId, event.key === "ArrowUp" ? "up" : "down")
        return
      }

      if (event.key === "Escape") {
        onSelect(null)
      }
    },
    [disabled, onIndent, onMove, onOutdent, onSelect, selectedId],
  )

  if (nodes.length === 0) {
    return (
      <div className="py-10">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("Menu đang trống — chọn nội dung ở cột bên trái rồi bấm “Thêm vào menu”.")}
        />
      </div>
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={handleKeyDown}>
      <Tree
        blockNode
        draggable={disabled ? false : { icon: <HolderOutlined className="text-gray-400" /> }}
        defaultExpandAll
        selectedKeys={selectedId === null ? [] : [selectedId]}
        treeData={treeData}
        allowDrop={allowDrop}
        onDrop={handleDrop}
        onSelect={keys => onSelect(keys.length > 0 ? Number(keys[0]) : null)}
        className="menu-structure-tree"
      />
    </div>
  )
}

export default MenuStructureTree
